import React, { useState } from "react";
import "./../comparativeAnalysis.less";

// 相态中文映射
const phaseMap: Record<string, string> = {
  V: "气相",
  L: "液相",
  VL: "气液混合",
};

// 温度单位选项
const temperatureUnits = ["℃", "K"];

// 压力单位选项
const pressureUnits = ["Pa", "bar", "KPa", "MPa", "atm"];

interface StreamResultProps {
  multiResults: any[];
  transInputs?: any[]; // 每个任务对应的 input 数据
}

export default function StreamsDataTable({ multiResults, transInputs }: StreamResultProps) {
  const [temperatureUnit, setTemperatureUnit] = useState("℃");
  const [pressureUnit, setPressureUnit] = useState("MPa");

  // 安全格式化
  const safeToFixed = (value: any, digits: number = 2) => {
    if (value === undefined || value === null || isNaN(value)) return "-";
    return Number(value).toFixed(digits);
  };

  // ====================== 核心修复：生成分组数据行 ======================
  // 输入：单个流股数据 + 所属任务
  // 输出：格式化后的一行数据 ([流股名_任务名, 相态, 摩尔流量, 质量流量...])
  const createTaskRow = (stream: any, task: any) => {
    const row = [];
    // 1. 第一列：流股名称 + 任务ID (如图2效果)
    row.push(`${stream.name}_任务${task.taskId}`);
    // 2. 第二列：相态
    row.push(phaseMap[stream.phase] || "未知");

    // 3. 动态计算数值（完全基于 stream 对象，修复数据错位）
    // 注意：这里不再使用 index，而是直接使用当前 stream 的数据
    const T = temperatureUnit === "℃" ? stream.T_C : stream.T_K;
    const P = {
      Pa: stream.P_Pa, bar: stream.P_bar, KPa: stream.P_KPa,
      MPa: stream.P_MPa, atm: stream.P_atm
    }[pressureUnit];

    // 修复：摩尔流量和质量流量也需要保留两位小数（如图1/图2）
    row.push(
      safeToFixed(stream.f_mol_per_h, 2),     // 摩尔流量
      safeToFixed(stream.f_kg_per_h, 2),      // 质量流量
      safeToFixed(T, 2),                      // 温度
      safeToFixed(P, 4),                      // 压力
      safeToFixed(stream.vf, 4),              // 气化率
      safeToFixed(stream.H)                  // 焓
    );
    return row;
  };

  // ====================== 生成表格数据（适配最终结构） ======================
  const getTableData = (streamType: "inputStreams" | "outStreams") => {
    if (!multiResults || multiResults.length === 0) return [];

    const allRows: any[] = [];
    const firstTask = multiResults[0];
    const baseStreams = firstTask[streamType] || [];

    // 遍历每一个基础流股（以第一个任务的流股列表为准）
    baseStreams.forEach((stream, streamIndex) => {
      // 遍历每一个任务，获取对应任务的流股数据
      multiResults.forEach((task) => {
        // 关键修复：从当前任务中获取对应索引的流股数据
        const targetStream = task[streamType]?.[streamIndex];
        if (targetStream) {
          allRows.push(createTaskRow(targetStream, task));
        }
      });
    });
    return allRows;
  };

  // ====================== 静态表头（完全匹配图3） ======================
  const tableHeaders = [
    "流股", "相态",
    "摩尔流量 (mol/h)", "质量流量 (kg/h)",
    "温度(°C)", "压力(MPa)",
    "气化率", "焓 (J/mol)"
  ];

  const inputData = getTableData("inputStreams");
  const outData = getTableData("outStreams");

  return (
    <div className="stream-result-container">
      {/* 单位切换 */}
      <div className="unit-selector-container">
        <div className="unit-selector">
          <span className="unit-selector-label">温度单位：</span>
          <div className="unit-buttons">
            {temperatureUnits.map((unit) => (
              <button
                key={unit}
                className={`unit-button ${temperatureUnit === unit ? "active" : ""}`}
                onClick={() => setTemperatureUnit(unit)}
              >
                {unit}
              </button>
            ))}
          </div>
        </div>
        <div className="unit-selector">
          <span className="unit-selector-label">压力单位：</span>
          <div className="unit-buttons">
            {pressureUnits.map((unit) => (
              <button
                key={unit}
                className={`unit-button ${pressureUnit === unit ? "active" : ""}`}
                onClick={() => setPressureUnit(unit)}
              >
                {unit}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 输入流股 */}
      <div className="table-container">
        <h3 className="stream-title">输入流股</h3>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr className="table-header">{tableHeaders.map((h, i) => <th key={i} className="table-header-cell">{h}</th>)}</tr>
            </thead>
            <tbody>
              {inputData.length > 0 ? (
                inputData.map((row, i) => (
                  <tr key={i} className="table-row">
                    {row.map((cell, j) => <td key={j} className="table-cell">{cell}</td>)}
                  </tr>
                ))
              ) : (
                <tr className="empty-row"><td colSpan={tableHeaders.length} className="empty-cell">暂无输入流股数据</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 输出流股 */}
        <h3 className="stream-title">输出流股</h3>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr className="table-header">{tableHeaders.map((h, i) => <th key={i} className="table-header-cell">{h}</th>)}</tr>
            </thead>
            <tbody>
              {outData.length > 0 ? (
                outData.map((row, i) => (
                  <tr key={i} className="table-row">
                    {row.map((cell, j) => <td key={j} className="table-cell">{cell}</td>)}
                  </tr>
                ))
              ) : (
                <tr className="empty-row"><td colSpan={tableHeaders.length} className="empty-cell">暂无输出流股数据</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}